import { CourierType } from 'const/CourierType';
import { PartitionType } from 'const/PartitionType';
import {
  fetchPartitioningCourier,
  fetchPartitioningList,
  fetchPartitioningZoneCount,
} from 'fetch/fetchPartitioning';
import { fetchAllRatings, ZoneRatingJson } from 'fetch/fetchRatings';
import { useCallback, useEffect, useRef, useState } from 'react';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { getAuthHeadersFromCookie } from '../lib/AuthClient/cookie';
import { settings } from '../settings';

export const usePartitionsAction = (activeDate: string) => {
  const message = (icon: SweetAlertIcon, title: string, html: string) => {
    Swal.fire({
      position: 'center',
      icon,
      title,
      showConfirmButton: false,
      timer: 1500,
      html,
    });
  };
  const errorMessage = (html: string) => {
    message('error', 'There was an error', html);
  };
  const successMessage = (html: string) => {
    message('success', 'Success!', html);
  };

  const copy = async (fromDate: string, toDate: string, region: string) => {
    const regionPath = region.toLowerCase();
    const fetchRes = await fetch(
      `${settings.autobundlerUrl}/api/partitioning/${regionPath}/copy?from=${fromDate}&to=${toDate}`,
      {
        headers: {
          Authorization: getAuthHeadersFromCookie()?.Authorization,
        },
        method: 'POST',
      }
    );

    if (fetchRes.ok) {
      /* Returning the `true` as copied successfully */
      return true;
    } else {
      errorMessage(`<div>Failed to Auto-assign Couriers into Partitions</div>`);

      /* Returning the `false` as failed to copy */
      return false;
    }
  };

  const autoPartition = async (date: string, region: string) => {
    const regionPath = region.toLowerCase();
    const fetchRes = await fetch(
      `${settings.autobundlerUrl}/api/partitioning/${regionPath}/auto?date=${date}`,
      {
        headers: {
          Authorization: getAuthHeadersFromCookie()?.Authorization,
        },
        method: 'POST',
      }
    );

    if (fetchRes.ok) {
      console.info('Auto-partition has been running successfully');
      successMessage(`<div>Auto-partition has been running successfully</div>`);
    } else {
      errorMessage(`<div>Failed to do the Auto-partition</div>`);
    }
  };

  const autoAssignCouriers = async (date: string, region: string) => {
    const regionPath = region.toLowerCase();
    const fetchRes = await fetch(
      `${settings.autobundlerUrl}/api/partitioning/${regionPath}/${date}/assigncouriers`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeadersFromCookie()?.Authorization,
        },
        method: 'POST',
      }
    );

    if (fetchRes.ok) {
      successMessage(
        `<div>Auto-assign Couriers has been running successfully</div>`
      );
    } else {
      errorMessage(`<div>Failed to Auto-assign Couriers into Partitions</div>`);
    }
  };

  const add = async (date: string, region: string) => {
    const regionPath = region.toLowerCase();
    const fetchRes = await fetch(
      `${settings.autobundlerUrl}/api/partitioning/${regionPath}/${date}/partitions/create`,
      {
        headers: {
          Authorization: getAuthHeadersFromCookie()?.Authorization,
        },
        method: 'post',
      }
    );

    if (fetchRes.ok) {
      await fetchRes.json();
      successMessage(`<div>New partition has been added successfully.</div>`);

      return true;
    } else {
      errorMessage(`<div>Failed to add partition</div>`);

      return false;
    }
  };

  const editCourier = async (
    date: string,
    courier: CourierType,
    partition?: PartitionType
  ) => {
    const fetchRes = await fetch(
      `${settings.autobundlerUrl}/api/partitioning/${date}/partitions/courier-edit`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeadersFromCookie()?.Authorization,
        },
        method: 'post',
        body: JSON.stringify({
          courierId: parseInt(courier.id),
          newPartition: partition?.id || null,
        }),
      }
    );

    if (fetchRes.ok) {
      console.info(`The courier ${courier.id} has been edited successfully`);
    } else {
      errorMessage(`<div>Failed to edit the Partition Courier</div>`);
    }
  };

  const editZone = async (
    date: string,
    zoneId: string,
    newPartition?: PartitionType
  ) => {
    const fetchRes = await fetch(
      `${settings.autobundlerUrl}/api/partitioning/${date}/partitions/zone-edit`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeadersFromCookie()?.Authorization,
        },
        method: 'post',
        body: JSON.stringify({
          zoneId,
          newPartition: newPartition?.id || null,
        }),
      }
    );

    if (fetchRes.ok) {
      console.info(`The zone ${zoneId} has been edited successfully`);
    } else {
      errorMessage(`<div>Failed to edit the Partition Zone</div>`);
    }
  };

  return {
    copy,
    autoPartition,
    autoAssignCouriers,
    add,
    editCourier,
    editZone,
  };
};
